import type { ActivityDetail } from './api'

const SHARE_ORIGIN = 'https://app.kuniri.it'

export function activityShareUrl(
  activityId: string,
  inviteToken: string | null,
): string {
  const path = `/share/activities/${encodeURIComponent(activityId)}`
  const query = inviteToken
    ? `?invite=${encodeURIComponent(inviteToken)}`
    : ''
  return `${SHARE_ORIGIN}${path}${query}`
}

export function whatsappShareUrl(text: string): string {
  return `https://wa.me/?text=${encodeURIComponent(text)}`
}

export function smsShareUrl(text: string): string {
  return `sms:?body=${encodeURIComponent(text)}`
}

function calendarStamp(value: Date): string {
  return value
    .toISOString()
    .replaceAll('-', '')
    .replaceAll(':', '')
    .replace(/\.\d{3}Z$/, 'Z')
}

function escapeICS(value: string): string {
  return value
    .replaceAll('\\', '\\\\')
    .replaceAll('\n', '\\n')
    .replaceAll(';', '\\;')
    .replaceAll(',', '\\,')
}

/**
 * Builds a standard iCalendar payload. The mobile button downloads this as an
 * .ics file so iOS/Android can import it into the device's default calendar
 * instead of opening Google Calendar in a browser tab.
 */
export function activityICS(
  activity: Pick<
    ActivityDetail,
    | 'id'
    | 'title'
    | 'startAt'
    | 'endAt'
    | 'locationName'
    | 'city'
    | 'meetingPoint'
    | 'description'
    | 'inviteToken'
  >,
  now: Date = new Date(),
): string | null {
  if (!activity.startAt) return null
  const start = new Date(activity.startAt)
  if (Number.isNaN(start.getTime())) return null
  const end = activity.endAt ? new Date(activity.endAt) : null
  const safeEnd =
    end && !Number.isNaN(end.getTime()) && end > start
      ? end
      : new Date(start.getTime() + 60 * 60 * 1000)

  const location = [
    activity.locationName,
    activity.meetingPoint,
    activity.city,
  ]
    .filter((value): value is string => Boolean(value?.trim()))
    .join(', ')
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Kuniri//Activity//IT',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${activity.id}@kuniri.it`,
    `DTSTAMP:${calendarStamp(now)}`,
    `DTSTART:${calendarStamp(start)}`,
    `DTEND:${calendarStamp(safeEnd)}`,
    `SUMMARY:${escapeICS(activity.title)}`,
    `DESCRIPTION:${escapeICS(activity.description.trim())}`,
    `LOCATION:${escapeICS(location)}`,
    `URL:${activityShareUrl(activity.id, activity.inviteToken)}`,
    'STATUS:CONFIRMED',
    'END:VEVENT',
    'END:VCALENDAR',
  ]
  return `${lines.join('\r\n')}\r\n`
}

function icalStampCompact(value: Date): string {
  // The original strips the fractional seconds after compacting, so
  // `20261201T190000.000Z` becomes `20261201T190000Z`.
  return value
    .toISOString()
    .replace(/\.\d{3}Z$/, 'Z')
    .replaceAll('-', '')
    .replaceAll(':', '')
}

/**
 * Google Calendar template link, byte-for-byte the same behavior as the
 * kuniri-original `detail_screen.dart` (`launchUrl` on calendar.google.com).
 */
export function googleCalendarUrl(
  activity: Pick<ActivityDetail, 'title' | 'startAt' | 'endAt' | 'locationName' | 'city' | 'description'>,
): string | null {
  const start = activity.startAt ? new Date(activity.startAt) : null
  if (!start || Number.isNaN(start.getTime())) return null
  const end = activity.endAt ? new Date(activity.endAt) : null
  const safeEnd =
    end && !Number.isNaN(end.getTime()) && end > start
      ? end
      : new Date(start.getTime() + 60 * 60 * 1000)

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: activity.title,
    dates: `${icalStampCompact(start)}/${icalStampCompact(safeEnd)}`,
  })
  const location = [activity.locationName, activity.city]
    .filter((value): value is string => Boolean(value?.trim()))
    .join(', ')
  if (location) params.set('location', location)
  if (activity.description.trim()) params.set('details', activity.description)
  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

/** Downloads the .ics and returns false when the activity has no valid start. */
export function downloadActivityICS(activity: Parameters<typeof activityICS>[0]): boolean {
  const payload = activityICS(activity)
  if (!payload || typeof document === 'undefined') return false
  const blob = new Blob([payload], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `kuniri-${activity.id}.ics`
  anchor.rel = 'noopener'
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000)
  return true
}
